import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNotification } from '../../contexts/NotificationContext';
import { api, apiLongTimeout } from '../../services/api';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  images?: string[];
  generatedImages?: string[];
  isGenerating?: boolean;
  taskId?: string;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  last_message_preview?: string;
}

interface DraftData {
  type?: string;
  price?: number;
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  parking_spaces?: number;
  suites?: number;
  address?: {
    street?: string;
    number?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  features?: string[];
  amenities?: string[];
  title?: string;
  description?: string;
  location?: string;
  transaction_type?: string;
  [key: string]: any;
}

interface CopilotProps {
  isOpen: boolean;
  onToggle: () => void;
}

const CopilotContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${({ $isOpen }) => $isOpen ? '0' : '-400px'};
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -2px 0 10px rgba(0,0,0,0.1);
  z-index: 1000;
  transition: right 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CopilotHeader = styled.div`
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: rgba(255,255,255,0.1);
  }
`;

const ClearButton = styled.button`
  background: rgba(255,255,255,0.2);
  border: none;
  color: white;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background: rgba(255,255,255,0.3);
  }
`;

const DraftIndicator = styled.div`
  padding: 8px 16px;
  background: #fff3cd;
  border-bottom: 1px solid #ffc107;
  font-size: 12px;
  color: #856404;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DraftBadge = styled.span`
  background: #ffc107;
  color: #856404;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 11px;
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;
  align-self: ${({ $isUser }) => $isUser ? 'flex-end' : 'flex-start'};
  background: ${({ $isUser, theme }) => $isUser ? theme.colors.primary : '#f1f3f4'};
  color: ${({ $isUser }) => $isUser ? 'white' : '#333'};
  word-wrap: break-word;
  white-space: pre-wrap;

  a {
    color: ${({ $isUser }) => $isUser ? '#fff' : '#007bff'};
    text-decoration: underline;
    font-weight: 600;

    &:hover {
      opacity: 0.8;
    }
  }

  strong {
    font-weight: 600;
  }
`;

const MessageImages = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const MessageImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #ddd;
`;

const MessageTime = styled.div`
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
`;

const ChatInput = styled.div`
  padding: 16px;
  border-top: 1px solid #eee;
  background: #f9f9f9;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TextInput = styled.textarea`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  resize: none;
  min-height: 60px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ImageUploadContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #ddd;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff4444;
  color: white;
  border: none;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FileInput = styled.input`
  display: none;
`;

const FileButton = styled.label`
  padding: 8px 12px;
  background: #f1f3f4;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: #e8eaed;
  }
`;

const SendButton = styled.button`
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ToggleFloatingButton = styled.button<{ $isOpen: boolean }>`
  position: fixed;
  top: 50%;
  right: ${({ $isOpen }) => $isOpen ? '400px' : '20px'};
  transform: translateY(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 999;
  transition: right 0.3s ease;

  &:hover {
    transform: translateY(-50%) scale(1.05);
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f1f3f4;
  border-radius: 16px;
  align-self: flex-start;
  max-width: 80%;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #666;
    animation: loading 1.4s ease-in-out infinite both;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }

  @keyframes loading {
    0%, 80%, 100% {
      transform: scale(0);
    } 40% {
      transform: scale(1);
    }
  }
`;

const GeneratedImagesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const GeneratedImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #4CAF50;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const GeneratingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
  margin-top: 8px;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const QuickActionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #eee;
`;

const QuickActionButton = styled.button`
  padding: 6px 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 16px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const UsageBadge = styled.div`
  padding: 4px 8px;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 12px;
  font-size: 10px;
  margin-left: auto;
`;

// Sidebar de conversas
const ConversationSidebar = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: ${({ $isOpen }) => $isOpen ? '0' : '-280px'};
  width: 280px;
  height: 100%;
  background: #f8f9fa;
  border-right: 1px solid #e0e0e0;
  z-index: 10;
  transition: left 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 16px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const NewConversationButton = styled.button`
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    opacity: 0.9;
  }
`;

const ConversationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const ConversationItem = styled.div<{ $isActive: boolean }>`
  padding: 12px;
  margin-bottom: 4px;
  background: ${({ $isActive }) => $isActive ? '#e3f2fd' : 'white'};
  border: 1px solid ${({ $isActive }) => $isActive ? '#1976d2' : '#e0e0e0'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $isActive }) => $isActive ? '#e3f2fd' : '#f5f5f5'};
  }
`;

const ConversationTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConversationPreview = styled.div`
  font-size: 11px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConversationMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  font-size: 10px;
  color: #999;
`;

const DeleteConversationButton = styled.button`
  background: none;
  border: none;
  color: #ff4444;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 4px;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

const ToggleSidebarButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  margin-right: 8px;

  &:hover {
    opacity: 0.8;
  }
`;

const EmptyConversations = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 12px;
`;

const INITIAL_MESSAGE: Message = {
  id: '1',
  type: 'assistant',
  content: 'Olá! Sou seu assistente imobiliário com IA.\n\nPosso ajudar você a:\n- Cadastrar imóveis (descreva ou envie fotos)\n- Editar imóveis existentes\n- Buscar imóveis\n- **Gerar imagens** com IA\n- **Staging virtual** em fotos vazias\n- Criar **marcas d\'água**\n\nDiga o que precisa ou use os botões de ação rápida!',
  timestamp: new Date()
};

const Copilot: React.FC<CopilotProps> = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [inputText, setInputText] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [draftData, setDraftData] = useState<DraftData | null>(null);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);

  // Estados para histórico de conversas
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);

  const { showNotification } = useNotification();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Carregar lista de conversas
  const loadConversations = async () => {
    try {
      setIsLoadingConversations(true);
      const response = await api.get('/copilot/conversations?limit=50');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Carregar conversas ao abrir o copilot
  useEffect(() => {
    if (isOpen) {
      loadConversations();
    }
  }, [isOpen]);

  // Carregar uma conversa específica
  const loadConversation = async (conversationId: string) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/copilot/conversations/${conversationId}`);
      const conversation = response.data;

      // Converter mensagens do backend para o formato do frontend
      const loadedMessages: Message[] = conversation.messages.map((msg: any) => ({
        id: msg.id,
        type: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: new Date(msg.created_at),
        images: msg.images || [],
        generatedImages: msg.extra_data?.generated_images || []
      }));

      // Adicionar mensagem inicial se não houver mensagens
      if (loadedMessages.length === 0) {
        loadedMessages.push(INITIAL_MESSAGE);
      }

      setMessages(loadedMessages);
      setCurrentConversationId(conversationId);

      // Restaurar contexto se houver
      if (conversation.context) {
        if (conversation.context.draft_data) {
          setDraftData(conversation.context.draft_data);
        }
        if (conversation.context.editing_property_id) {
          setEditingPropertyId(conversation.context.editing_property_id);
        }
      }

      setIsSidebarOpen(false);
    } catch (error) {
      console.error('Erro ao carregar conversa:', error);
      showNotification('Erro ao carregar conversa', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Criar nova conversa
  const startNewConversation = () => {
    setMessages([INITIAL_MESSAGE]);
    setCurrentConversationId(null);
    setDraftData(null);
    setEditingPropertyId(null);
    setIsSidebarOpen(false);
  };

  // Deletar conversa
  const deleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir esta conversa?')) return;

    try {
      await api.delete(`/copilot/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c.id !== conversationId));

      // Se deletou a conversa atual, iniciar nova
      if (currentConversationId === conversationId) {
        startNewConversation();
      }

      showNotification('Conversa excluída', 'success');
    } catch (error) {
      console.error('Erro ao deletar conversa:', error);
      showNotification('Erro ao deletar conversa', 'error');
    }
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Ontem';
    } else if (days < 7) {
      return `${days} dias atrás`;
    } else {
      return date.toLocaleDateString('pt-BR');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Função para converter URLs em links clicáveis
  const formatMessageContent = (content: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let formattedContent = content;

    formattedContent = formattedContent.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });

    formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    return formattedContent;
  };

  // Resumo do rascunho atual
  const getDraftSummary = () => {
    if (!draftData) return '';
    const parts = [];
    if (draftData.type) parts.push(draftData.type);
    if (draftData.price) parts.push(`R$ ${draftData.price.toLocaleString('pt-BR')}`);
    if (draftData.bedrooms) parts.push(`${draftData.bedrooms}Q`);
    if (draftData.address?.neighborhood) parts.push(draftData.address.neighborhood);
    return parts.join(' | ');
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length + selectedImages.length > 10) {
      showNotification('Máximo de 10 imagens por mensagem', 'error');
      return;
    }

    setSelectedImages(prev => [...prev, ...imageFiles]);
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (image: File) => {
    try {
      const formData = new FormData();
      formData.append('file', image);

      const response = await api.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data && response.data.media && response.data.media.url) {
        return response.data.media.url;
      } else {
        throw new Error('Resposta de upload inválida');
      }
    } catch (error) {
      throw new Error('Erro ao fazer upload da imagem');
    }
  };

  const processWithAI = async (text: string, imageUrls: string[]) => {
    try {
      // Enviar contexto com draft_data e editing_property_id
      const context: any = {};

      if (draftData && Object.keys(draftData).length > 0) {
        context.draft_data = draftData;
      }

      if (editingPropertyId) {
        context.editing_property_id = editingPropertyId;
      }

      // Usar apiLongTimeout para operações de IA que podem demorar (edição de imagem, etc)
      const response = await apiLongTimeout.post('/copilot/process', {
        message: text,
        images: imageUrls,
        action: 'analyze_property',
        context: context,
        conversation_id: currentConversationId
      });

      // Atualizar conversation_id se retornado (nova conversa criada)
      if (response.data.conversation_id && !currentConversationId) {
        setCurrentConversationId(response.data.conversation_id);
        // Recarregar lista de conversas
        loadConversations();
      }

      return response.data;
    } catch (error) {
      console.error('Erro ao processar com IA:', error);
      throw error;
    }
  };

  const clearDraft = () => {
    setDraftData(null);
    setEditingPropertyId(null);
    showNotification('Rascunho limpo', 'success');
  };

  const clearConversation = () => {
    startNewConversation();
    showNotification('Nova conversa iniciada', 'success');
  };

  // Ações rápidas para tools de imagem
  const handleQuickAction = (action: string) => {
    const actions: { [key: string]: string } = {
      'generate': 'Gera uma foto de sala de estar moderna com sofá cinza e luz natural',
      'staging': 'Aplica staging virtual moderno nesta foto',
      'watermark': 'Cria uma marca d\'água elegante para a imobiliária',
      'enhance': 'Melhora a qualidade desta foto',
      'cadastrar': 'Quero cadastrar um novo imóvel',
    };

    const message = actions[action];
    if (message) {
      setInputText(message);
    }
  };

  const handleSend = async () => {
    if (!inputText.trim() && selectedImages.length === 0) return;

    const messageId = Date.now().toString();
    const userMessage: Message = {
      id: messageId,
      type: 'user',
      content: inputText,
      timestamp: new Date(),
      images: selectedImages.map(img => URL.createObjectURL(img))
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Upload das imagens se houver
      let imageUrls: string[] = [];
      if (selectedImages.length > 0) {
        try {
          imageUrls = await Promise.all(selectedImages.map(async (image) => {
            const url = await handleImageUpload(image);
            return url;
          }));
          imageUrls = imageUrls.filter(url => url && typeof url === 'string');
        } catch (uploadError) {
          showNotification('Erro ao fazer upload das imagens', 'error');
          setIsLoading(false);
          return;
        }
      }

      // Processar com IA
      const aiResponse = await processWithAI(inputText, imageUrls);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: aiResponse.message,
        timestamp: new Date(),
        generatedImages: aiResponse.generated_images || [],
        taskId: aiResponse.task_id,
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Se tem imagens geradas, mostrar notificação
      if (aiResponse.generated_images && aiResponse.generated_images.length > 0) {
        showNotification(`${aiResponse.generated_images.length} imagem(ns) gerada(s)!`, 'success');
      }

      // Atualizar draft_data se retornado
      if (aiResponse.draft_data !== undefined) {
        if (aiResponse.draft_data === null || Object.keys(aiResponse.draft_data).length === 0) {
          setDraftData(null);
        } else {
          setDraftData(aiResponse.draft_data);
        }
      }

      // Limpar rascunho se imóvel foi criado
      if (aiResponse.property_created) {
        setDraftData(null);
        setEditingPropertyId(null);
        showNotification('Imóvel cadastrado com sucesso!', 'success');
      } else if (aiResponse.property_updated) {
        setDraftData(null);
        setEditingPropertyId(null);
        showNotification('Imóvel atualizado com sucesso!', 'success');
      }

      // Se a ação foi clear_draft
      if (aiResponse.action === 'clear_draft') {
        setDraftData(null);
        setEditingPropertyId(null);
      }

    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Desculpe, ocorreu um erro ao processar sua solicitação. Tente novamente.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      showNotification('Erro ao processar solicitação', 'error');
    }

    setInputText('');
    setSelectedImages([]);
    setIsLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <ToggleFloatingButton $isOpen={isOpen} onClick={onToggle}>
        {isOpen ? '✕' : '🤖'}
      </ToggleFloatingButton>

      <CopilotContainer $isOpen={isOpen}>
        {/* Sidebar de conversas */}
        <ConversationSidebar $isOpen={isSidebarOpen}>
          <SidebarHeader>
            <SidebarTitle>Conversas</SidebarTitle>
            <NewConversationButton onClick={startNewConversation}>
              ➕ Nova
            </NewConversationButton>
          </SidebarHeader>
          <ConversationList>
            {isLoadingConversations ? (
              <EmptyConversations>Carregando...</EmptyConversations>
            ) : conversations.length === 0 ? (
              <EmptyConversations>
                Nenhuma conversa salva ainda.
                <br />
                Suas conversas aparecerão aqui.
              </EmptyConversations>
            ) : (
              conversations.map(conv => (
                <ConversationItem
                  key={conv.id}
                  $isActive={currentConversationId === conv.id}
                  onClick={() => loadConversation(conv.id)}
                >
                  <ConversationTitle>{conv.title || 'Conversa sem título'}</ConversationTitle>
                  {conv.last_message_preview && (
                    <ConversationPreview>{conv.last_message_preview}</ConversationPreview>
                  )}
                  <ConversationMeta>
                    <span>{formatDate(conv.updated_at)}</span>
                    <DeleteConversationButton
                      onClick={(e) => deleteConversation(conv.id, e)}
                      title="Excluir conversa"
                    >
                      🗑️
                    </DeleteConversationButton>
                  </ConversationMeta>
                </ConversationItem>
              ))
            )}
          </ConversationList>
        </ConversationSidebar>

        <CopilotHeader>
          <HeaderTitle>
            <ToggleSidebarButton
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              title={isSidebarOpen ? 'Fechar histórico' : 'Ver histórico'}
            >
              {isSidebarOpen ? '◀' : '☰'}
            </ToggleSidebarButton>
            🤖 Copilot
          </HeaderTitle>
          <HeaderActions>
            <ClearButton onClick={clearConversation} title="Nova conversa">
              ➕ Nova
            </ClearButton>
            <ToggleButton onClick={onToggle}>
              ✕
            </ToggleButton>
          </HeaderActions>
        </CopilotHeader>

        {draftData && Object.keys(draftData).length > 0 && (
          <DraftIndicator>
            <div>
              <DraftBadge>Rascunho</DraftBadge>
              <span style={{ marginLeft: 8 }}>{getDraftSummary()}</span>
            </div>
            <button
              onClick={clearDraft}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              title="Limpar rascunho"
            >
              ✕
            </button>
          </DraftIndicator>
        )}

        <QuickActionsContainer>
          <QuickActionButton onClick={() => handleQuickAction('cadastrar')}>
            🏠 Cadastrar
          </QuickActionButton>
          <QuickActionButton onClick={() => handleQuickAction('generate')}>
            🎨 Gerar Imagem
          </QuickActionButton>
          <QuickActionButton onClick={() => handleQuickAction('staging')}>
            🛋️ Staging
          </QuickActionButton>
          <QuickActionButton onClick={() => handleQuickAction('enhance')}>
            ✨ Melhorar
          </QuickActionButton>
          <QuickActionButton onClick={() => handleQuickAction('watermark')}>
            💧 Marca d'água
          </QuickActionButton>
        </QuickActionsContainer>

        <ChatMessages>
          {messages.map((message) => (
            <div key={message.id}>
              <MessageBubble $isUser={message.type === 'user'}>
                <div dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content) }} />
                {message.images && message.images.length > 0 && (
                  <MessageImages>
                    {message.images.map((img, idx) => (
                      <MessageImage key={idx} src={img} alt={`Imagem ${idx + 1}`} />
                    ))}
                  </MessageImages>
                )}
                {message.generatedImages && message.generatedImages.length > 0 && (
                  <GeneratedImagesContainer>
                    <div style={{ width: '100%', fontSize: '11px', color: '#4CAF50', marginBottom: '4px' }}>
                      🎨 Imagens geradas por IA:
                    </div>
                    {message.generatedImages.map((img, idx) => (
                      <GeneratedImage
                        key={idx}
                        src={img}
                        alt={`Gerada ${idx + 1}`}
                        onClick={() => window.open(img, '_blank')}
                        title="Clique para abrir em tamanho original"
                      />
                    ))}
                  </GeneratedImagesContainer>
                )}
                {message.isGenerating && (
                  <GeneratingIndicator>
                    🎨 Gerando imagem... Isso pode levar alguns segundos.
                  </GeneratingIndicator>
                )}
                <MessageTime>
                  {message.timestamp.toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </MessageTime>
              </MessageBubble>
            </div>
          ))}

          {isLoading && (
            <LoadingIndicator>
              <LoadingDots>
                <span></span>
                <span></span>
                <span></span>
              </LoadingDots>
              Analisando com IA...
            </LoadingIndicator>
          )}

          <div ref={messagesEndRef} />
        </ChatMessages>

        <ChatInput>
          <InputContainer>
            {selectedImages.length > 0 && (
              <ImageUploadContainer>
                {selectedImages.map((image, index) => (
                  <ImagePreview key={index}>
                    <PreviewImage
                      src={URL.createObjectURL(image)}
                      alt={`Preview ${index + 1}`}
                    />
                    <RemoveImageButton onClick={() => removeImage(index)}>
                      ✕
                    </RemoveImageButton>
                  </ImagePreview>
                ))}
              </ImageUploadContainer>
            )}

            <TextInput
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={draftData ? "Continue descrevendo ou diga 'cadastrar'..." : "Descreva o imóvel ou faça uma pergunta..."}
              disabled={isLoading}
            />

            <ActionButtons>
              <FileInput
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageSelect}
                id="copilot-file-input"
              />
              <FileButton htmlFor="copilot-file-input">
                📸 Fotos
              </FileButton>

              <SendButton
                onClick={handleSend}
                disabled={isLoading || (!inputText.trim() && selectedImages.length === 0)}
              >
                {isLoading ? '⏳' : '📤'} Enviar
              </SendButton>
            </ActionButtons>
          </InputContainer>
        </ChatInput>
      </CopilotContainer>
    </>
  );
};

export default Copilot;
